import { type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSession } from '../lib/auth-client';
import { api, type ApiSuccess } from '../lib/api';

/**
 * 🛠 بوابة وضع الصيانة
 *
 * - عند تفعيل `site_maintenance` في الإعدادات:
 *   • الزائر العادي / المستخدم العادي → يرى صفحة "تحت الصيانة"
 *   • الأدمن / السوبر أدمن → يدخل بشكل عادي مع شريط علوي يبيّن أن الوضع مفعّل
 * - الـ /login يبقى متاحاً دائماً ليستطيع الأدمن الدخول.
 */

type SettingsMap = Record<string, string | null>;

const ALLOWED_ROLES = ['ADMIN', 'SUPER_ADMIN'];

const ALLOWED_PATHS = ['/login', '/forgot-password', '/reset-password'];

export function MaintenanceGate({ children }: { children: ReactNode }) {
  const { data: settingsRes } = useQuery({
    queryKey: ['public-settings'],
    queryFn: () =>
      api
        .get<ApiSuccess<SettingsMap>>('/api/settings')
        .catch(() => ({ data: {} as SettingsMap })),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const { data: session } = useSession();

  const isMaintenance = (() => {
    const v = settingsRes?.data?.site_maintenance;
    return v === '1' || v === 'true';
  })();

  const role = (session?.user as { role?: string } | undefined)?.role ?? '';
  const isPrivileged = ALLOWED_ROLES.includes(role);

  // مسموح دائماً (تسجيل الدخول)
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  const isAllowedPath = ALLOWED_PATHS.includes(path);

  // ✋ زائر/مستخدم عادي ووضع الصيانة مفعّل → اعرض صفحة الصيانة
  if (isMaintenance && !isPrivileged && !isAllowedPath) {
    return <MaintenancePage />;
  }

  // 🟧 أدمن داخل وضع الصيانة → اعرض شريط علوي + الموقع كامل
  return (
    <>
      {isMaintenance && isPrivileged && <MaintenanceBar />}
      {children}
    </>
  );
}

function MaintenanceBar() {
  return (
    <div className="bg-amber-500 text-amber-950 border-b-2 border-amber-700 shadow-md">
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-center gap-3 flex-wrap text-center">
        <span className="text-lg">🛠️</span>
        <div className="text-xs md:text-sm font-black">
          وضع الصيانة مُفعَّل — الموقع غير متاح للزوار حالياً (أنت تراه لأنك أدمن)
        </div>
        <a
          href="/admin/settings"
          className="px-3 py-1 rounded-md bg-amber-950 text-amber-200 hover:bg-black text-[11px] font-bold transition"
        >
          ⚙️ إعدادات الموقع
        </a>
      </div>
    </div>
  );
}

function MaintenancePage() {
  return (
    <div
      dir="rtl"
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: 'linear-gradient(135deg, #1a3151 0%, #1f3b66 50%, #2a4d80 100%)' }}
    >
      <div className="max-w-lg w-full bg-white rounded-3xl shadow-2xl p-10 text-center">
        <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 text-white flex items-center justify-center text-4xl mb-5 shadow-lg">
          🛠️
        </div>
        <h1 className="text-3xl font-black text-navy-dark mb-2">الموقع تحت الصيانة</h1>
        <p className="text-ink-secondary leading-relaxed mb-6">
          نقوم حالياً بأعمال صيانة وتحسينات لمنصة <b>شركة صورة العقارية</b>.
          <br />
          سنعود قريباً بإذن الله — شكراً لصبركم.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6 text-start">
          <a
            href="tel:0114993666"
            className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 hover:border-gold transition"
          >
            <span className="text-2xl">📞</span>
            <div>
              <div className="text-[11px] text-ink-muted">للضرورة</div>
              <div className="font-black text-navy-dark text-sm" dir="ltr">
                0114993666
              </div>
            </div>
          </a>
          <a
            href="https://wa.me/966114993666"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 hover:border-green-500 transition"
          >
            <span className="text-2xl">💬</span>
            <div>
              <div className="text-[11px] text-ink-muted">واتساب</div>
              <div className="font-black text-navy-dark text-sm">تواصل معنا</div>
            </div>
          </a>
        </div>
        <a
          href="/login"
          className="inline-block text-xs text-ink-muted hover:text-navy-dark underline"
        >
          أنا مدير — تسجيل الدخول
        </a>
      </div>
    </div>
  );
}
